import * as Sentry from '@sentry/react';
import axios from 'axios';
import { getSiteName } from 'utils/env';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SITE_NAME = getSiteName().trim();

let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export const setAuthToken = (token: string | null) => {
  authToken = token;
};

export const onUnauthorized = (handler: () => void) => {
  unauthorizedHandler = handler;
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

const toShortString = (value: unknown, max = 240): string => {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  return raw.slice(0, max);
};

const resolveRequestUrl = (baseURL?: string, url?: string): string => {
  const base = (baseURL || '').trim();
  const target = (url || '').trim();
  if (!target) return base;
  if (/^https?:\/\//i.test(target)) return target;
  if (!base) return target;
  return `${base.replace(/\/$/, '')}/${target.replace(/^\//, '')}`;
};

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  if (SITE_NAME && !config.headers['X-Site-Name']) {
    config.headers['X-Site-Name'] = SITE_NAME;
  }
  if (authToken) {
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status || 0);
    const method = String(error?.config?.method || 'GET').toUpperCase();
    const requestUrl = resolveRequestUrl(error?.config?.baseURL, error?.config?.url);
    const responseData = error?.response?.data;
    const responseError =
      typeof responseData?.error === 'string'
        ? responseData.error
        : typeof responseData?.message === 'string'
          ? responseData.message
          : toShortString(responseData);

    Sentry.logger.error('httpclient error', {
      'http.method': method,
      'http.url': toShortString(requestUrl, 300),
      'http.status_code': status,
      'http.network_error': status === 0,
      'http.code': toShortString(error?.code),
      'http.message': toShortString(error?.message),
      'http.response_error': toShortString(responseError),
      'site.name': SITE_NAME || 'unknown',
    });

    if (error.response?.status === 401 && unauthorizedHandler) {
      unauthorizedHandler();
    }
    return Promise.reject(error);
  }
);

export default api;
